import React, { PropsWithChildren, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Size } from 'ff-canvas';
import { useResize } from '../../hooks/useResize';
import { Icon } from '../icons/Icon';
import { ExpandButton } from './ExpandButton';
import { ScrollContainer } from './ScrollContainer';
import styles from './ExpandableScrollContainer.module.scss';

export const ExpandableScrollContainer = ({
    children, className, initialHeight = 300
}: Props) => {

    const { t } = useTranslation();

    const [expanded, setExpanded] = useState(false);

    const contentRef = useRef<HTMLDivElement>(null);
    const contentSize = useResize(contentRef);

    const containerMaxHeight = contentSize
        ? determineHeight(initialHeight, contentSize, expanded)
        : initialHeight;

    const showExpand = contentSize
        ? contentSize.height > containerMaxHeight || expanded
        : false;

    return (
        <>
            <ScrollContainer maxHeight={containerMaxHeight} className={className}>
                <div ref={contentRef}>
                    {children}
                </div>
            </ScrollContainer>

            {showExpand && (
                <div className={styles.buttonContainer}>
                    {!expanded ? (
                        <ExpandButton onClick={() => setExpanded(true)}>
                            <Icon type='general-ui-chevron-down' className={styles.buttonIcon} />
                            {t('commons.expand')}
                        </ExpandButton>
                    ) : (
                        <ExpandButton onClick={() => setExpanded(false)}>
                            <Icon type='general-ui-chevron-up' className={styles.buttonIcon} />
                            {t('commons.collapse')}
                        </ExpandButton>
                    )}
                </div>
            )}
        </>
    );
};

type Props = PropsWithChildren<{
    initialHeight?: number;
    className?: string;
}>;

const determineHeight = (
    initialHeight: number,
    contentSize: Size,
    expanded: boolean
) => {

    const maxHeight = initialHeight * 2;

    const computedHeight = expanded
        ? Math.min(contentSize.height, maxHeight)
        : initialHeight;

    if (contentSize.height < computedHeight * 1.2) {
        return contentSize.height; // Avoid pointless scrollbars
    }

    return computedHeight;
};